import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Users, Shield, Tag, BarChart3, Plus, Trash2, 
  Crown, Star, ArrowLeft, RefreshCw, Copy, Check, Key,
  Link2, Percent, Gift, Calendar, DollarSign, UserPlus, Wrench, Search, CreditCard, AlertTriangle, Lock, Megaphone, Mail
} from 'lucide-react';
import { toast } from 'sonner';
import { apiService } from '../lib/api';
import { formatDate } from '../lib/dateUtils';
import { DemoRequestModal } from '../components/DemoRequestModal';

const Admin = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  const [demoLinkOpen, setDemoLinkOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('user');
    navigate('/login');
  };
  
  // Data states
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [promoCodes, setPromoCodes] = useState([]);
  const [inviteCodes, setInviteCodes] = useState([]);
  const [betaStats, setBetaStats] = useState(null);
  const [affiliates, setAffiliates] = useState([]);
  const [discountCodes, setDiscountCodes] = useState([]);
  
  // New promo code form
  const [newPromoCode, setNewPromoCode] = useState({
    code: '',
    discount_percent: 0,
    extended_trial_days: 0,
    max_uses: -1,
    expires_days: 30
  });
  
  // New invite code form
  const [newInviteUses, setNewInviteUses] = useState(1);
  const [newInviteExpires, setNewInviteExpires] = useState('');
  
  // New affiliate form
  const [newAffiliate, setNewAffiliate] = useState({
    name: '',
    email: '',
    commission_percent: 30
  });
  
  // New discount code form
  const [newDiscountCode, setNewDiscountCode] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: 10,
    max_uses: -1,
    expires_days: 90
  });
  
  // Free access modal state
  const [showFreeAccessModal, setShowFreeAccessModal] = useState(false);
  const [freeAccessUser, setFreeAccessUser] = useState(null);
  const [freeAccessDays, setFreeAccessDays] = useState(30);
  const [freeAccessTier, setFreeAccessTier] = useState('pro');
  
  // Restore bid state
  const [restoreBidData, setRestoreBidData] = useState({
    bidRequestId: '',
    contractorEmail: '',
    totalAmount: '',
    notes: ''
  });
  const [isRestoringBid, setIsRestoringBid] = useState(false);
  
  // Bid lookup state
  const [bidLookupEmail, setBidLookupEmail] = useState('');
  const [bidLookupProject, setBidLookupProject] = useState('');
  const [bidLookupResults, setBidLookupResults] = useState([]);
  const [isLookingUpBids, setIsLookingUpBids] = useState(false);
  
  // Duplicate subscription state
  const [duplicateSubs, setDuplicateSubs] = useState([]);
  const [isLoadingDuplicates, setIsLoadingDuplicates] = useState(false);
  const [cancelingSubId, setCancelingSubId] = useState(null);
  
  const [copiedCode, setCopiedCode] = useState(null);
  const [errorLogs, setErrorLogs] = useState([]);
  const [errorOpenCount, setErrorOpenCount] = useState(0);
  const [loadingErrors, setLoadingErrors] = useState(false);

  const loadErrorLogs = async () => {
    setLoadingErrors(true);
    try {
      const res = await apiService.getErrorLogs(false);
      setErrorLogs(res.data?.errors || []);
      setErrorOpenCount(res.data?.open_count || 0);
    } catch (e) {
      toast.error('Failed to load error log');
    } finally {
      setLoadingErrors(false);
    }
  };

  const handleResolveError = async (id) => {
    try {
      await apiService.resolveErrorLog(id);
      setErrorLogs((prev) => prev.filter((e) => e.id !== id));
      setErrorOpenCount((c) => Math.max(0, c - 1));
      toast.success('Marked resolved');
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  useEffect(() => {
    if (activeTab === 'errors') loadErrorLogs();
  }, [activeTab]);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const userData = JSON.parse(storedUser);
      setUser(userData);
      
      // Check if user is admin
      if (!userData.is_admin) {
        toast.error('Admin access required');
        navigate('/dashboard');
        return;
      }
      
      loadData(userData.id);
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const loadData = async (adminId) => {
    setLoading(true);
    try {
      const [usersRes, statsRes, promoRes, inviteRes, betaRes, affiliatesRes, discountRes] = await Promise.all([
        apiService.adminGetUsers(adminId),
        apiService.adminGetStats(adminId),
        apiService.adminGetPromoCodes(adminId),
        apiService.getInviteCodes().catch(() => ({ data: [] })),
        apiService.getBetaStats().catch(() => ({ data: null })),
        apiService.getAffiliates().catch(() => ({ data: [] })),
        apiService.getDiscountCodes().catch(() => ({ data: [] }))
      ]);
      
      setUsers(usersRes.data || []);
      setStats(statsRes.data || { total_users: 0, total_projects: 0, beta_testers: 0, vip_users: 0 });
      setPromoCodes(promoRes.data || []);
      setInviteCodes(inviteRes.data || []);
      setBetaStats(betaRes.data);
      setAffiliates(affiliatesRes.data || []);
      setDiscountCodes(discountRes.data || []);
    } catch (error) {
      console.error('Failed to load admin data:', error);
      // Set default values so the page still renders
      setUsers([]);
      setStats({ total_users: 0, total_projects: 0, beta_testers: 0, vip_users: 0 });
      setPromoCodes([]);
      setInviteCodes([]);
      setAffiliates([]);
      setDiscountCodes([]);
      
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load admin data. Please try refreshing.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggleBetaTester = async (userId, currentValue) => {
    try {
      await apiService.adminUpdateUser(userId, user.id, { is_beta_tester: !currentValue });
      toast.success(currentValue ? 'Beta tester access removed' : 'Beta tester access granted');
      loadData(user.id);
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleToggleVIP = async (userId, currentValue) => {
    try {
      await apiService.adminUpdateUser(userId, user.id, { is_vip: !currentValue });
      toast.success(currentValue ? 'VIP access removed' : 'VIP access granted');
      loadData(user.id);
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleToggleAdmin = async (userId, currentValue) => {
    try {
      await apiService.adminUpdateUser(userId, user.id, { make_admin: !currentValue });
      toast.success(currentValue ? 'Admin access removed' : 'Admin access granted');
      loadData(user.id);
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleDeleteUser = async (userToDelete) => {
    if (userToDelete.is_admin) {
      toast.error('Cannot delete admin users. Remove admin privileges first.');
      return;
    }
    
    if (userToDelete.id === user.id) {
      toast.error('Cannot delete your own account');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete user "${userToDelete.email}"?\n\nThis will permanently delete:\n- All their projects\n- All phases and payments\n- All documents\n- All team memberships\n\nThis action cannot be undone.`
    );
    
    if (!confirmed) return;

    try {
      await apiService.adminDeleteUser(userToDelete.id, user.id);
      toast.success(`User ${userToDelete.email} deleted successfully`);
      loadData(user.id);
    } catch (error) {
      console.error('Delete user error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleRevokeAccess = async (userToRevoke) => {
    if (userToRevoke.is_admin) {
      toast.error('Cannot revoke access from another admin. Remove admin privileges first.');
      return;
    }
    if (userToRevoke.id === user.id) {
      toast.error('Cannot revoke your own access');
      return;
    }
    const confirmed = window.confirm(
      `Revoke FREE access for "${userToRevoke.email}"?\n\nThis will:\n- Remove Beta Tester / VIP / Free Access flags\n- Force them through the payment gate on next login\n- Mark trial as expired\n\nUse this to clean up users who bypassed the paywall during the OAuth bug. They will need to subscribe to continue using DrawReady.`
    );
    if (!confirmed) return;

    try {
      await apiService.adminRevokeAccess(userToRevoke.id, user.id);
      toast.success(`Free access revoked for ${userToRevoke.email}`);
      loadData(user.id);
    } catch (error) {
      console.error('Revoke access error:', error);
      toast.error(error.response?.data?.detail || 'Failed to revoke access');
    }
  };

  const handleCreatePromoCode = async () => {
    if (!newPromoCode.code) {
      toast.error('Please enter a promo code');
      return;
    }
    
    try {
      await apiService.adminCreatePromoCode(user.id, newPromoCode);
      toast.success('Promo code created');
      setNewPromoCode({
        code: '',
        discount_percent: 0,
        extended_trial_days: 0,
        max_uses: -1,
        expires_days: 30
      });
      loadData(user.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create promo code');
    }
  };

  const handleDeletePromoCode = async (codeId) => {
    if (!window.confirm('Delete this promo code?')) return;
    
    try {
      await apiService.adminDeletePromoCode(codeId, user.id);
      toast.success('Promo code deleted');
      loadData(user.id);
    } catch (error) {
      toast.error('Failed to delete promo code');
    }
  };

  // Invite Code Functions
  const handleCreateInviteCode = async () => {
    try {
      const expiresDays = newInviteExpires ? parseInt(newInviteExpires) : null;
      const response = await apiService.createInviteCode(newInviteUses, expiresDays);
      toast.success(`Invite code created: ${response.data.code}`);
      setNewInviteUses(1);
      setNewInviteExpires('');
      loadData(user.id);
    } catch (error) {
      toast.error('Failed to create invite code');
    }
  };

  const handleDeleteInviteCode = async (code) => {
    try {
      await apiService.deleteInviteCode(code);
      toast.success('Invite code deactivated');
      loadData(user.id);
    } catch (error) {
      toast.error('Failed to delete invite code');
    }
  };

  const handleApproveUser = async (userId) => {
    try {
      await apiService.approveUser(userId);
      toast.success('User approved');
      loadData(user.id);
    } catch (error) {
      toast.error('Failed to approve user');
    }
  };

  const handleDenyUser = async (userId) => {
    try {
      await apiService.denyUser(userId);
      toast.success('User access revoked');
      loadData(user.id);
    } catch (error) {
      toast.error('Failed to deny user');
    }
  };

  // Grant Free Access Function
  const handleGrantFreeAccess = async () => {
    if (!freeAccessUser || freeAccessDays <= 0) {
      toast.error('Please select a valid duration');
      return;
    }
    
    try {
      await apiService.adminUpdateUser(freeAccessUser.id, user.id, {
        free_access_days: freeAccessDays,
        subscription_tier: freeAccessTier
      });
      toast.success(`Granted ${freeAccessDays} days free access to ${freeAccessUser.email}`);
      setShowFreeAccessModal(false);
      setFreeAccessUser(null);
      setFreeAccessDays(30);
      loadData(user.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant free access');
    }
  };

  // Restore Bid Response Function
  const handleRestoreBid = async () => {
    if (!restoreBidData.bidRequestId || !restoreBidData.contractorEmail || !restoreBidData.totalAmount) {
      toast.error('Please fill in Bid Request ID, Contractor Email, and Total Amount');
      return;
    }
    
    setIsRestoringBid(true);
    try {
      const result = await apiService.adminFixBidResponse(restoreBidData.bidRequestId, {
        contractor_email: restoreBidData.contractorEmail,
        total_amount: parseFloat(restoreBidData.totalAmount),
        notes: restoreBidData.notes || undefined
      });
      toast.success(`Bid restored! Amount: $${parseFloat(restoreBidData.totalAmount).toLocaleString()}`);
      setRestoreBidData({ bidRequestId: '', contractorEmail: '', totalAmount: '', notes: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore bid');
    } finally {
      setIsRestoringBid(false);
    }
  };

  // Bid Request Lookup Function
  const handleLookupBidRequests = async () => {
    if (!bidLookupEmail && !bidLookupProject) {
      toast.error('Please enter a contractor email or project name');
      return;
    }
    
    setIsLookingUpBids(true);
    setBidLookupResults([]);
    try {
      const params = {};
      if (bidLookupEmail) params.contractor_email = bidLookupEmail;
      if (bidLookupProject) params.project_name = bidLookupProject;
      
      const result = await apiService.adminLookupBidRequests(params);
      setBidLookupResults(result.data?.results || []);
      
      if ((result.data?.results || []).length === 0) {
        toast.info('No bid requests found for this search');
      } else {
        toast.success(`Found ${result.data.count} bid request(s)`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to lookup bid requests');
    } finally {
      setIsLookingUpBids(false);
    }
  };

  // Use bid request from lookup results
  const handleUseBidRequest = (bidRequest) => {
    setRestoreBidData({
      ...restoreBidData,
      bidRequestId: bidRequest.bid_request_id,
      contractorEmail: bidLookupEmail || restoreBidData.contractorEmail,
      totalAmount: bidRequest.total_amount ? String(bidRequest.total_amount) : ''
    });
    toast.success('Bid Request ID copied to restore form');
  };

  // Duplicate Subscription Functions
  const handleScanDuplicates = async () => {
    setIsLoadingDuplicates(true);
    setDuplicateSubs([]);
    try {
      const result = await apiService.adminGetDuplicateSubscriptions();
      setDuplicateSubs(result.data?.duplicates || []);
      if ((result.data?.duplicates || []).length === 0) {
        toast.success(`Scanned ${result.data?.total_users_scanned || 0} users - no duplicates found!`);
      } else {
        toast.warning(`Found ${result.data?.users_with_duplicates || 0} user(s) with duplicate subscriptions`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to scan for duplicates');
    } finally {
      setIsLoadingDuplicates(false);
    }
  };

  const handleCancelSubscription = async (subscriptionId, immediate = false) => {
    if (!confirm(`Are you sure you want to ${immediate ? 'immediately cancel' : 'schedule cancellation for'} subscription ${subscriptionId}?`)) {
      return;
    }
    
    setCancelingSubId(subscriptionId);
    try {
      await apiService.adminCancelSubscription(subscriptionId, immediate);
      toast.success(`Subscription ${immediate ? 'canceled' : 'scheduled for cancellation'}`);
      // Refresh the list
      handleScanDuplicates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelingSubId(null);
    }
  };

  // Affiliate Functions
  const handleCreateAffiliate = async () => {
    if (!newAffiliate.name || !newAffiliate.email) {
      toast.error('Please enter name and email');
      return;
    }
    
    try {
      await apiService.createAffiliate(newAffiliate);
      toast.success('Affiliate created');
      setNewAffiliate({ name: '', email: '', commission_percent: 30 });
      loadData(user.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create affiliate');
    }
  };

  const handleToggleAffiliateStatus = async (affiliateId, currentStatus) => {
    try {
      await apiService.updateAffiliate(affiliateId, { 
        status: currentStatus === 'active' ? 'inactive' : 'active' 
      });
      toast.success(currentStatus === 'active' ? 'Affiliate deactivated' : 'Affiliate activated');
      loadData(user.id);
    } catch (error) {
      toast.error('Failed to update affiliate');
    }
  };

  const handleDeleteAffiliate = async (affiliateId) => {
    if (!window.confirm('Delete this affiliate?')) return;
    
    try {
      await apiService.deleteAffiliate(affiliateId);
      toast.success('Affiliate deleted');
      loadData(user.id);
    } catch (error) {
      toast.error('Failed to delete affiliate');
    }
  };

  // Discount Code Functions
  const handleCreateDiscountCode = async () => {
    if (!newDiscountCode.code) {
      toast.error('Please enter a discount code');
      return;
    }
    
    try {
      await apiService.createDiscountCode({
        ...newDiscountCode,
        expires_days: newDiscountCode.expires_days > 0 ? newDiscountCode.expires_days : null
      });
      toast.success('Discount code created');
      setNewDiscountCode({
        code: '',
        description: '',
        discount_type: 'percentage',
        discount_value: 10,
        max_uses: -1,
        expires_days: 90
      });
      loadData(user.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create discount code');
    }
  };

  const handleToggleDiscountStatus = async (codeId, currentStatus) => {
    try {
      await apiService.updateDiscountCode(codeId, { 
        status: currentStatus === 'active' ? 'inactive' : 'active' 
      });
      toast.success(currentStatus === 'active' ? 'Code deactivated' : 'Code activated');
      loadData(user.id);
    } catch (error) {
      toast.error('Failed to update discount code');
    }
  };

  const handleDeleteDiscountCode = async (codeId) => {
    if (!window.confirm('Delete this discount code?')) return;
    
    try {
      await apiService.deleteDiscountCode(codeId);
      toast.success('Discount code deleted');
      loadData(user.id);
    } catch (error) {
      toast.error('Failed to delete discount code');
    }
  };

  const getAffiliateLink = (code) => {
    return `${window.location.origin}/signup?ref=${code}`;
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-700 mb-4">Loading user data...</p>
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 p-4 shadow-sm">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout}
              className="text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-blue-600" />
              <h1 className="text-xl font-bold text-slate-900">Admin Panel</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => setDemoLinkOpen(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="admin-send-demo-link-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              Send demo link
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => loadData(user.id)}
              className="border-slate-300 hover:bg-slate-50"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_users || 0}</p>
                  <p className="text-sm text-slate-500">Total Users</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <BarChart3 className="w-8 h-8 text-green-500" />
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_projects || 0}</p>
                  <p className="text-sm text-slate-500">Total Projects</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Star className="w-8 h-8 text-yellow-500" />
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.beta_testers || 0}</p>
                  <p className="text-sm text-slate-500">Beta Testers</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Crown className="w-8 h-8 text-purple-500" />
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.vip_users || 0}</p>
                  <p className="text-sm text-slate-500">VIP Users</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 text-emerald-500" />
                <div>
                  <p className="text-2xl font-bold text-slate-900" data-testid="stat-active-30d">{stats?.active_users_30d || 0}</p>
                  <p className="text-sm text-slate-500">Active (30 days)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <DollarSign className="w-8 h-8 text-indigo-500" />
                <div>
                  <p className="text-2xl font-bold text-slate-900" data-testid="stat-draw-requests">{stats?.total_draw_requests || 0}</p>
                  <p className="text-sm text-slate-500">Draw Requests</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <UserPlus className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="text-2xl font-bold text-slate-900" data-testid="stat-new-30d">{stats?.new_users_30d || 0}</p>
                  <p className="text-sm text-slate-500">New Users (30 days)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-8 h-8 text-orange-500" />
                <div>
                  <p className="text-2xl font-bold text-slate-900" data-testid="stat-new-7d">{stats?.new_users_7d || 0}</p>
                  <p className="text-sm text-slate-500">New Users (7 days)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex gap-2 mb-6 flex-wrap">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/announcements')}
            className="border-blue-300 text-blue-700 hover:bg-blue-50"
            data-testid="admin-announcements-link"
          >
            <Megaphone className="w-4 h-4 mr-2" />
            Feature Announcements
          </Button>
          <Button
            variant={activeTab === 'users' ? 'default' : 'outline'}
            onClick={() => setActiveTab('users')}
            className={activeTab !== 'users' ? 'border-slate-300 text-slate-700 hover:bg-slate-50' : ''}
          >
            <Users className="w-4 h-4 mr-2" />
            Users
          </Button>
          <Button
            variant={activeTab === 'affiliates' ? 'default' : 'outline'}
            onClick={() => setActiveTab('affiliates')}
            className={activeTab !== 'affiliates' ? 'border-slate-300 text-slate-700 hover:bg-slate-50' : ''}
          >
            <Link2 className="w-4 h-4 mr-2" />
            Affiliates ({affiliates.length})
          </Button>
          <Button
            variant={activeTab === 'discounts' ? 'default' : 'outline'}
            onClick={() => setActiveTab('discounts')}
            className={activeTab !== 'discounts' ? 'border-slate-300 text-slate-700 hover:bg-slate-50' : ''}
          >
            <Gift className="w-4 h-4 mr-2" />
            Discount Codes ({discountCodes.length})
          </Button>
          <Button
            variant={activeTab === 'promo' ? 'default' : 'outline'}
            onClick={() => setActiveTab('promo')}
            className={activeTab !== 'promo' ? 'border-slate-300 text-slate-700 hover:bg-slate-50' : ''}
          >
            <Tag className="w-4 h-4 mr-2" />
            Promo Codes
          </Button>
          <Button
            variant={activeTab === 'invites' ? 'default' : 'outline'}
            onClick={() => setActiveTab('invites')}
            className={activeTab !== 'invites' ? 'border-slate-300 text-slate-700 hover:bg-slate-50' : ''}
          >
            <Key className="w-4 h-4 mr-2" />
            Invite Codes ({inviteCodes.length})
          </Button>
          <Button
            variant={activeTab === 'tools' ? 'default' : 'outline'}
            onClick={() => setActiveTab('tools')}
            className={activeTab !== 'tools' ? 'border-slate-300 text-slate-700 hover:bg-slate-50' : ''}
          >
            <Wrench className="w-4 h-4 mr-2" />
            Tools
          </Button>
          <Button
            variant={activeTab === 'errors' ? 'default' : 'outline'}
            onClick={() => setActiveTab('errors')}
            className={activeTab !== 'errors' ? 'border-slate-300 text-slate-700 hover:bg-slate-50' : ''}
            data-testid="admin-tab-errors"
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Errors{errorOpenCount > 0 ? ` (${errorOpenCount})` : ''}
          </Button>
        </div>

        {/* Beta Stats Banner */}
        {betaStats && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <span className="bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded-full">BETA</span>
                <span className="text-amber-700 font-medium">Early Access Mode</span>
              </div>
              <div className="flex gap-6 text-sm">
                <div>
                  <span className="text-slate-600">Signup Limit:</span>
                  <span className="text-slate-900 ml-2 font-bold">{betaStats.signup_limit}</span>
                </div>
                <div>
                  <span className="text-slate-600">Spots Remaining:</span>
                  <span className="text-green-600 ml-2 font-bold">{betaStats.spots_remaining}</span>
                </div>
                <div>
                  <span className="text-slate-600">Active Invites:</span>
                  <span className="text-blue-600 ml-2 font-bold">{betaStats.active_invites}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
                <Users className="w-5 h-5" />
                All Users ({users.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left p-3 text-slate-600 font-medium">User</th>
                      <th className="text-left p-3 text-slate-600 font-medium">Status</th>
                      <th className="text-left p-3 text-slate-600 font-medium">Last Active</th>
                      <th className="text-left p-3 text-slate-600 font-medium">Signed Up</th>
                      <th className="text-center p-3 text-slate-600 font-medium">Projects</th>
                      <th className="text-center p-3 text-slate-600 font-medium">Draws</th>
                      <th className="text-center p-3 text-slate-600 font-medium">Beta</th>
                      <th className="text-center p-3 text-slate-600 font-medium">VIP</th>
                      <th className="text-center p-3 text-slate-600 font-medium">Admin</th>
                      <th className="text-center p-3 text-slate-600 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-3">
                          <div>
                            <p className="font-medium text-slate-900">{u.name}</p>
                            <p className="text-sm text-slate-500">{u.email}</p>
                          </div>
                        </td>
                        <td className="p-3">
                          <Badge 
                            variant="outline" 
                            className={
                              u.is_beta_tester ? 'border-yellow-500 text-yellow-600' :
                              u.is_vip ? 'border-purple-500 text-purple-600' :
                              u.subscription_tier === 'trial' ? 'border-blue-500 text-blue-600' :
                              u.subscription_tier === 'pro' ? 'border-green-500 text-green-600' :
                              'border-slate-300 text-slate-500'
                            }
                          >
                            {u.subscription_display || u.subscription_tier || 'None'}
                          </Badge>
                        </td>
                        <td className="p-3">
                          {u.is_online ? (
                            <div className="flex items-center gap-1.5" data-testid={`user-online-${u.id}`}>
                              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                              <span className="text-sm font-medium text-emerald-600">Online now</span>
                            </div>
                          ) : u.last_active ? (
                            <div className="flex items-center gap-1.5">
                              <span className={`w-2 h-2 rounded-full ${u.active_last_30d ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                              <span className="text-sm text-slate-600">{new Date(u.last_active).toLocaleString()}</span>
                            </div>
                          ) : (
                            <span className="text-sm text-slate-400">Never</span>
                          )}
                          {u.last_login && (
                            <p className="text-xs text-slate-400 mt-0.5">Last login {new Date(u.last_login).toLocaleDateString()}</p>
                          )}
                        </td>
                        <td className="p-3">
                          <span className="text-sm text-slate-600">
                            {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          <span className="text-slate-700">{u.project_count || 0}</span>
                        </td>
                        <td className="p-3 text-center">
                          <span className="text-slate-700">{u.draw_request_count || 0}</span>
                        </td>
                        <td className="p-3 text-center">
                          <Switch
                            checked={u.is_beta_tester || false}
                            onCheckedChange={() => handleToggleBetaTester(u.id, u.is_beta_tester)}
                          />
                        </td>
                        <td className="p-3 text-center">
                          <Switch
                            checked={u.is_vip || false}
                            onCheckedChange={() => handleToggleVIP(u.id, u.is_vip)}
                          />
                        </td>
                        <td className="p-3 text-center">
                          <Switch
                            checked={u.is_admin || false}
                            onCheckedChange={() => handleToggleAdmin(u.id, u.is_admin)}
                            disabled={u.id === user.id}
                          />
                        </td>
                        <td className="p-3 text-center flex gap-1 justify-center">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setFreeAccessUser(u); setShowFreeAccessModal(true); }}
                            disabled={u.id === user.id}
                            className="text-green-600 hover:text-green-700 hover:bg-green-50 border-green-300"
                            title="Grant free access"
                            data-testid={`admin-grant-free-${u.id}`}
                          >
                            <Gift className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRevokeAccess(u)}
                            disabled={u.id === user.id || u.is_admin}
                            className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 border-amber-300 disabled:opacity-30"
                            title="Revoke free access (force payment gate)"
                            data-testid={`admin-revoke-access-${u.id}`}
                          >
                            <Lock className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteUser(u)}
                            disabled={u.id === user.id || u.is_admin}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50 disabled:opacity-30"
                            title={u.id === user.id ? "Cannot delete yourself" : u.is_admin ? "Remove admin first" : "Delete user"}
                            data-testid={`admin-delete-user-${u.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Free Access Modal */}
        <Dialog open={showFreeAccessModal} onOpenChange={setShowFreeAccessModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-green-600" />
                Grant Free Access
              </DialogTitle>
            </DialogHeader>
            {freeAccessUser && (
              <div className="space-y-4">
                <div className="bg-slate-100 p-3 rounded-lg">
                  <p className="font-medium">{freeAccessUser.name || freeAccessUser.email}</p>
                  <p className="text-sm text-slate-600">{freeAccessUser.email}</p>
                </div>
                
                <div>
                  <label className="text-sm text-slate-600 mb-1 block">Duration (days)</label>
                  <Input
                    type="number"
                    min="1"
                    max="365"
                    value={freeAccessDays}
                    onChange={(e) => setFreeAccessDays(parseInt(e.target.value) || 30)}
                    className="bg-white border-slate-300"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Common: 30 (1 month), 90 (3 months), 365 (1 year)
                  </p>
                </div>
                
                <div>
                  <label className="text-sm text-slate-600 mb-1 block">Plan Level</label>
                  <select
                    value={freeAccessTier}
                    onChange={(e) => setFreeAccessTier(e.target.value)}
                    className="w-full h-10 px-3 bg-white border border-slate-300 rounded-md text-slate-900"
                  >
                    <option value="basic">Basic (2 projects)</option>
                    <option value="pro">Pro (5 projects)</option>
                    <option value="enterprise">Enterprise (Unlimited)</option>
                  </select>
                </div>
                
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={() => setShowFreeAccessModal(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button onClick={handleGrantFreeAccess} className="flex-1 bg-green-600 hover:bg-green-700">
                    <Gift className="w-4 h-4 mr-2" />
                    Grant Access
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Affiliates Tab */}
        {activeTab === 'affiliates' && (
          <div className="space-y-6">
            {/* Create New Affiliate */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
                  <UserPlus className="w-5 h-5" />
                  Create Affiliate Partner
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="text-sm text-slate-600 mb-1 block">Name</label>
                    <Input
                      placeholder="John Smith"
                      value={newAffiliate.name}
                      onChange={(e) => setNewAffiliate({...newAffiliate, name: e.target.value})}
                      className="bg-white border-slate-300"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-600 mb-1 block">Email</label>
                    <Input
                      type="email"
                      placeholder="john@example.com"
                      value={newAffiliate.email}
                      onChange={(e) => setNewAffiliate({...newAffiliate, email: e.target.value})}
                      className="bg-white border-slate-300"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-600 mb-1 block">Commission %</label>
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={newAffiliate.commission_percent}
                      onChange={(e) => setNewAffiliate({...newAffiliate, commission_percent: parseInt(e.target.value) || 30})}
                      className="bg-white border-slate-300"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={handleCreateAffiliate} className="w-full">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Affiliate
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-slate-500 mt-3">
                  Affiliates earn {newAffiliate.commission_percent}% of the first year membership for each referral.
                </p>
              </CardContent>
            </Card>

            {/* Existing Affiliates */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
                  <Link2 className="w-5 h-5" />
                  Active Affiliates ({affiliates.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {affiliates.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No affiliates created yet</p>
                ) : (
                  <div className="space-y-4">
                    {affiliates.map((affiliate) => (
                      <div 
                        key={affiliate.id} 
                        className="p-4 bg-slate-50 rounded-lg border border-slate-200"
                      >
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium text-slate-900">{affiliate.name}</h4>
                              <Badge 
                                variant="outline" 
                                className={affiliate.status === 'active' ? 'border-green-500 text-green-600' : 'border-slate-300 text-slate-500'}
                              >
                                {affiliate.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-500">{affiliate.email}</p>
                          </div>
                          <div className="flex gap-4 text-sm">
                            <div className="text-center">
                              <p className="text-slate-500">Commission</p>
                              <p className="font-bold text-green-600">{affiliate.commission_percent}%</p>
                            </div>
                            <div className="text-center">
                              <p className="text-slate-500">Referrals</p>
                              <p className="font-bold text-slate-900">{affiliate.total_referrals || 0}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-slate-500">Clicks</p>
                              <p className="font-bold text-slate-900">{affiliate.total_clicks || 0}</p>
                            </div>
                          </div>
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                          <div className="flex-1 bg-slate-100 border border-slate-200 rounded px-3 py-2 text-sm font-mono text-blue-600 truncate">
                            {getAffiliateLink(affiliate.code)}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(getAffiliateLink(affiliate.code))}
                            className="shrink-0"
                          >
                            {copiedCode === getAffiliateLink(affiliate.code) ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                          <Switch
                            checked={affiliate.status === 'active'}
                            onCheckedChange={() => handleToggleAffiliateStatus(affiliate.id, affiliate.status)}
                          />
                          <span className="text-sm text-slate-600">Active</span>
                          <div className="flex-1" />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteAffiliate(affiliate.id)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Discount Codes Tab */}
        {activeTab === 'discounts' && (
          <div className="space-y-6">
            {/* Create New Discount Code */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
                  <Gift className="w-5 h-5" />
                  Create Discount Code
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <div>
                    <label className="text-sm text-slate-600 mb-1 block">Code</label>
                    <Input
                      placeholder="SAVE20"
                      value={newDiscountCode.code}
                      onChange={(e) => setNewDiscountCode({...newDiscountCode, code: e.target.value.toUpperCase()})}
                      className="bg-white border-slate-300"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-600 mb-1 block">Type</label>
                    <select
                      value={newDiscountCode.discount_type}
                      onChange={(e) => setNewDiscountCode({...newDiscountCode, discount_type: e.target.value})}
                      className="w-full h-10 px-3 bg-white border border-slate-300 rounded-md text-slate-900"
                    >
                      <option value="percentage">Percentage Off</option>
                      <option value="fixed">Fixed $ Off</option>
                      <option value="free_months">Free Months</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-slate-600 mb-1 block">
                      {newDiscountCode.discount_type === 'percentage' ? 'Discount %' : 
                       newDiscountCode.discount_type === 'fixed' ? 'Amount $' : 'Months Free'}
                    </label>
                    <Input
                      type="number"
                      min="1"
                      max={newDiscountCode.discount_type === 'percentage' ? 100 : 999}
                      value={newDiscountCode.discount_value}
                      onChange={(e) => setNewDiscountCode({...newDiscountCode, discount_value: parseInt(e.target.value) || 0})}
                      className="bg-white border-slate-300"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-600 mb-1 block">Max Uses (-1 = unlimited)</label>
                    <Input
                      type="number"
                      value={newDiscountCode.max_uses}
                      onChange={(e) => setNewDiscountCode({...newDiscountCode, max_uses: parseInt(e.target.value) || -1})}
                      className="bg-white border-slate-300"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-600 mb-1 block">Expires (days, 0=never)</label>
                    <Input
                      type="number"
                      min="0"
                      value={newDiscountCode.expires_days}
                      onChange={(e) => setNewDiscountCode({...newDiscountCode, expires_days: parseInt(e.target.value) || 0})}
                      className="bg-white border-slate-300"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={handleCreateDiscountCode} className="w-full">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Code
                    </Button>
                  </div>
                </div>
                <div className="mt-3">
                  <label className="text-sm text-slate-600 mb-1 block">Description (optional)</label>
                  <Input
                    placeholder="Special launch discount"
                    value={newDiscountCode.description}
                    onChange={(e) => setNewDiscountCode({...newDiscountCode, description: e.target.value})}
                    className="bg-white border-slate-300"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Existing Discount Codes */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
                  <Percent className="w-5 h-5" />
                  Active Discount Codes ({discountCodes.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {discountCodes.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No discount codes created yet</p>
                ) : (
                  <div className="space-y-3">
                    {discountCodes.map((code) => (
                      <div 
                        key={code.id} 
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200 flex-wrap gap-3"
                      >
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <code className="bg-slate-100 border border-slate-200 px-3 py-1 rounded font-mono text-green-600">
                              {code.code}
                            </code>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyToClipboard(code.code)}
                              className="h-8 w-8 p-0"
                            >
                              {copiedCode === code.code ? (
                                <Check className="w-4 h-4 text-green-500" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            <Badge variant="outline" className={
                              code.discount_type === 'percentage' ? 'border-blue-500 text-blue-600' :
                              code.discount_type === 'fixed' ? 'border-purple-500 text-purple-600' :
                              'border-green-500 text-green-600'
                            }>
                              {code.discount_type === 'percentage' ? `${code.discount_value}% off` :
                               code.discount_type === 'fixed' ? `$${code.discount_value} off` :
                               `${code.discount_value} month${code.discount_value > 1 ? 's' : ''} free`}
                            </Badge>
                            <Badge 
                              variant="outline" 
                              className={code.status === 'active' ? 'border-green-500 text-green-600' : 'border-slate-300 text-slate-500'}
                            >
                              {code.status}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-slate-600">
                            Used: {code.times_used || 0}{code.max_uses > 0 ? `/${code.max_uses}` : ''}
                          </span>
                          {code.expires_at && (
                            <span className="text-sm text-slate-600 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              Expires {formatDate(code.expires_at)}
                            </span>
                          )}
                          <Switch
                            checked={code.status === 'active'}
                            onCheckedChange={() => handleToggleDiscountStatus(code.id, code.status)}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteDiscountCode(code.id)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Promo Codes Tab */}
        {activeTab === 'promo' && (
          <div className="space-y-6">
            {/* Create New Promo Code */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
                  <Plus className="w-5 h-5" />
                  Create Promo Code
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div>
                    <label className="text-sm text-slate-600 mb-1 block">Code</label>
                    <Input
                      placeholder="BETA50"
                      value={newPromoCode.code}
                      onChange={(e) => setNewPromoCode({...newPromoCode, code: e.target.value.toUpperCase()})}
                      className="bg-white border-slate-300"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-600 mb-1 block">Discount %</label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={newPromoCode.discount_percent}
                      onChange={(e) => setNewPromoCode({...newPromoCode, discount_percent: e.target.value === '' ? '' : parseInt(e.target.value) || ''})}
                      onBlur={(e) => { if (e.target.value === '' || isNaN(parseInt(e.target.value))) setNewPromoCode(prev => ({...prev, discount_percent: 0})); }}
                      className="bg-white border-slate-300"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-600 mb-1 block">Extended Trial Days</label>
                    <Input
                      type="number"
                      min="0"
                      value={newPromoCode.extended_trial_days}
                      onChange={(e) => setNewPromoCode({...newPromoCode, extended_trial_days: e.target.value === '' ? '' : parseInt(e.target.value) || ''})}
                      onBlur={(e) => { if (e.target.value === '' || isNaN(parseInt(e.target.value))) setNewPromoCode(prev => ({...prev, extended_trial_days: 0})); }}
                      className="bg-white border-slate-300"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-600 mb-1 block">Max Uses (-1 = unlimited)</label>
                    <Input
                      type="number"
                      value={newPromoCode.max_uses}
                      onChange={(e) => setNewPromoCode({...newPromoCode, max_uses: e.target.value === '' ? '' : parseInt(e.target.value) || ''})}
                      onBlur={(e) => { if (e.target.value === '' || isNaN(parseInt(e.target.value))) setNewPromoCode(prev => ({...prev, max_uses: -1})); }}
                      className="bg-white border-slate-300"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-600 mb-1 block">Expires (days)</label>
                    <Input
                      type="number"
                      min="1"
                      value={newPromoCode.expires_days}
                      onChange={(e) => setNewPromoCode({...newPromoCode, expires_days: e.target.value === '' ? '' : parseInt(e.target.value) || ''})}
                      onBlur={(e) => { if (e.target.value === '' || isNaN(parseInt(e.target.value))) setNewPromoCode(prev => ({...prev, expires_days: 30})); }}
                      className="bg-white border-slate-300"
                    />
                  </div>
                </div>
                <Button onClick={handleCreatePromoCode} className="mt-4">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Code
                </Button>
              </CardContent>
            </Card>

            {/* Existing Promo Codes */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
                  <Tag className="w-5 h-5" />
                  Active Promo Codes ({promoCodes.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {promoCodes.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No promo codes created yet</p>
                ) : (
                  <div className="space-y-3">
                    {promoCodes.map((promo) => (
                      <div 
                        key={promo.id} 
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200"
                      >
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <code className="bg-slate-100 border border-slate-200 px-3 py-1 rounded font-mono text-green-600">
                              {promo.code}
                            </code>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyToClipboard(promo.code)}
                              className="h-8 w-8 p-0"
                            >
                              {copiedCode === promo.code ? (
                                <Check className="w-4 h-4 text-green-500" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                          <div className="flex gap-2">
                            {promo.discount_percent > 0 && (
                              <Badge variant="outline" className="border-green-500 text-green-600">
                                {promo.discount_percent}% off
                              </Badge>
                            )}
                            {promo.extended_trial_days > 0 && (
                              <Badge variant="outline" className="border-blue-500 text-blue-600">
                                +{promo.extended_trial_days} days trial
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-slate-600">
                            Used: {promo.times_used}{promo.max_uses > 0 ? `/${promo.max_uses}` : ''}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeletePromoCode(promo.id)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Invite Codes Tab */}
        {activeTab === 'invites' && (
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
                  <Key className="w-5 h-5" />
                  Invite Codes
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {/* Create New Invite Code */}
              <div className="bg-slate-50 rounded-lg p-4 mb-6 border border-slate-200">
                <h4 className="text-sm font-medium mb-3 text-slate-900">Generate New Invite Code</h4>
                <div className="flex flex-wrap gap-3 items-end">
                  <div>
                    <label className="text-xs text-slate-500">Uses</label>
                    <input
                      type="number"
                      min="1"
                      value={newInviteUses}
                      onChange={(e) => setNewInviteUses(e.target.value === '' ? '' : parseInt(e.target.value) || '')}
                      onBlur={(e) => { if (e.target.value === '' || isNaN(parseInt(e.target.value))) setNewInviteUses(1); }}
                      className="w-20 px-3 py-2 bg-white border border-slate-300 rounded-md text-slate-900"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Expires (days)</label>
                    <input
                      type="number"
                      min="1"
                      placeholder="Never"
                      value={newInviteExpires}
                      onChange={(e) => setNewInviteExpires(e.target.value)}
                      className="w-24 px-3 py-2 bg-white border border-slate-300 rounded-md text-slate-900 placeholder-slate-400"
                    />
                  </div>
                  <Button onClick={handleCreateInviteCode} className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Generate Code
                  </Button>
                </div>
              </div>

              {/* Invite Codes List */}
              {inviteCodes.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Key className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No invite codes yet. Create one above.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {inviteCodes.map((code) => (
                    <div
                      key={code.id}
                      className={`flex items-center justify-between p-4 rounded-lg border ${
                        code.is_active && code.uses_remaining > 0
                          ? 'bg-slate-50 border-slate-200'
                          : 'bg-slate-100 border-slate-200 opacity-50'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div 
                          className="font-mono text-lg bg-slate-100 border border-slate-200 px-3 py-1 rounded cursor-pointer hover:bg-slate-200 flex items-center gap-2 text-slate-900"
                          onClick={() => copyToClipboard(code.code)}
                          title="Click to copy"
                        >
                          {code.code}
                          {copiedCode === code.code ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4 text-slate-500" />
                          )}
                        </div>
                        <div className="text-sm">
                          <span className="text-slate-600">{code.uses_remaining} uses left</span>
                          {code.used_by?.length > 0 && (
                            <span className="text-slate-500 ml-3">• Used by {code.used_by.length}</span>
                          )}
                          {code.expires_at && (
                            <span className="text-slate-500 ml-3">
                              • Expires {formatDate(code.expires_at)}
                            </span>
                          )}
                        </div>
                      </div>
                      {code.is_active && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteInviteCode(code.code)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tools Tab */}
        {activeTab === 'tools' && (
          <div className="space-y-6">
            {/* Bid Request Lookup */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
                  <Search className="w-5 h-5" />
                  Bid Request Lookup
                </CardTitle>
                <p className="text-sm text-slate-500 mt-1">
                  Search for bid requests by contractor email or project name to find the Bid Request ID
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-600 mb-1 block">Contractor Email</label>
                    <Input
                      type="email"
                      placeholder="contractor@example.com"
                      value={bidLookupEmail}
                      onChange={(e) => setBidLookupEmail(e.target.value)}
                      className="bg-white border-slate-300"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-600 mb-1 block">Project Name</label>
                    <Input
                      placeholder="e.g., New Build 38th"
                      value={bidLookupProject}
                      onChange={(e) => setBidLookupProject(e.target.value)}
                      className="bg-white border-slate-300"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <Button 
                    onClick={handleLookupBidRequests} 
                    disabled={isLookingUpBids}
                    className="bg-slate-700 hover:bg-slate-800"
                  >
                    {isLookingUpBids ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        Search Bid Requests
                      </>
                    )}
                  </Button>
                </div>
                
                {/* Search Results */}
                {bidLookupResults.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-sm font-medium text-slate-700 mb-3">Search Results ({bidLookupResults.length})</h4>
                    <div className="space-y-3 max-h-80 overflow-y-auto">
                      {bidLookupResults.map((bid, index) => (
                        <div 
                          key={bid.bid_request_id || index} 
                          className="p-4 border border-slate-200 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-slate-900">{bid.title}</span>
                                <Badge variant="outline" className="text-xs">
                                  {bid.trade_type}
                                </Badge>
                                {bid.has_response && (
                                  <Badge className="bg-green-100 text-green-800 text-xs">
                                    Has Response
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-slate-600">
                                Project: <span className="font-medium">{bid.project_name}</span>
                              </p>
                              <div className="mt-2 flex items-center gap-4 text-xs text-slate-500">
                                <span>ID: <code className="bg-slate-200 px-1 py-0.5 rounded font-mono text-xs">{bid.bid_request_id}</code></span>
                                {bid.total_amount && (
                                  <span>Amount: <span className="font-medium text-slate-700">${parseFloat(bid.total_amount).toLocaleString()}</span></span>
                                )}
                                <span>Status: {bid.status}</span>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleUseBidRequest(bid)}
                              className="ml-4 shrink-0"
                            >
                              <Check className="w-3 h-3 mr-1" />
                              Use This
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Restore Bid Response */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
                  <Wrench className="w-5 h-5" />
                  Restore Bid Response
                </CardTitle>
                <p className="text-sm text-slate-500 mt-1">
                  Fix bid response data that was accidentally cleared or corrupted
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-600 mb-1 block">Bid Request ID *</label>
                    <Input
                      placeholder="e.g., abc123-def456-..."
                      value={restoreBidData.bidRequestId}
                      onChange={(e) => setRestoreBidData({...restoreBidData, bidRequestId: e.target.value})}
                      className="bg-white border-slate-300 font-mono text-sm"
                    />
                    <p className="text-xs text-slate-400 mt-1">Find this in the URL when viewing the bid request</p>
                  </div>
                  <div>
                    <label className="text-sm text-slate-600 mb-1 block">Contractor Email *</label>
                    <Input
                      type="email"
                      placeholder="contractor@example.com"
                      value={restoreBidData.contractorEmail}
                      onChange={(e) => setRestoreBidData({...restoreBidData, contractorEmail: e.target.value})}
                      className="bg-white border-slate-300"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-600 mb-1 block">Total Amount *</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="102088.91"
                        value={restoreBidData.totalAmount}
                        onChange={(e) => setRestoreBidData({...restoreBidData, totalAmount: e.target.value})}
                        className="bg-white border-slate-300 pl-7"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-slate-600 mb-1 block">Notes / Scope (optional)</label>
                    <Input
                      placeholder="Scope of work description"
                      value={restoreBidData.notes}
                      onChange={(e) => setRestoreBidData({...restoreBidData, notes: e.target.value})}
                      className="bg-white border-slate-300"
                    />
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-4">
                  <Button 
                    onClick={handleRestoreBid} 
                    disabled={isRestoringBid}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {isRestoringBid ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Restoring...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Restore Bid
                      </>
                    )}
                  </Button>
                  <p className="text-sm text-slate-500">
                    This will update the bid response with the correct amount
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Duplicate Subscription Manager */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
                  <CreditCard className="w-5 h-5" />
                  Duplicate Subscription Manager
                </CardTitle>
                <p className="text-sm text-slate-500 mt-1">
                  Scan for and resolve duplicate Stripe subscriptions that may have been created before the fix was deployed
                </p>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <Button 
                    onClick={handleScanDuplicates} 
                    disabled={isLoadingDuplicates}
                    className="bg-slate-700 hover:bg-slate-800"
                  >
                    {isLoadingDuplicates ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Scanning Stripe...
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        Scan for Duplicates
                      </>
                    )}
                  </Button>
                </div>
                
                {/* Results */}
                {duplicateSubs.length > 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded-lg">
                      <AlertTriangle className="w-5 h-5" />
                      <span className="text-sm font-medium">Found {duplicateSubs.length} user(s) with duplicate subscriptions</span>
                    </div>
                    
                    {duplicateSubs.map((dup, index) => (
                      <div key={dup.user_id || index} className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="font-medium text-slate-900">{dup.name || 'Unknown'}</h4>
                            <p className="text-sm text-slate-600">{dup.email}</p>
                            {dup.company && <p className="text-xs text-slate-500">{dup.company}</p>}
                          </div>
                          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                            {dup.subscription_count} subscriptions
                          </Badge>
                        </div>
                        
                        <div className="space-y-2">
                          {dup.subscriptions.map((sub, subIndex) => {
                            const isDbSub = sub.subscription_id === dup.db_subscription_id;
                            const trialEnd = sub.trial_end ? new Date(sub.trial_end * 1000).toLocaleDateString() : null;
                            const created = new Date(sub.created * 1000).toLocaleDateString();
                            
                            return (
                              <div 
                                key={sub.subscription_id} 
                                className={`p-3 rounded-lg border ${isDbSub ? 'bg-green-50 border-green-200' : 'bg-white border-slate-200'}`}
                              >
                                <div className="flex justify-between items-center">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-sm">{sub.plan_name}</span>
                                      <Badge 
                                        variant="outline" 
                                        className={sub.status === 'trialing' ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'}
                                      >
                                        {sub.status}
                                      </Badge>
                                      {isDbSub && (
                                        <Badge className="bg-green-600 text-white text-xs">
                                          Primary
                                        </Badge>
                                      )}
                                      {sub.cancel_at_period_end && (
                                        <Badge variant="outline" className="bg-orange-50 text-orange-700">
                                          Canceling
                                        </Badge>
                                      )}
                                    </div>
                                    <div className="text-xs text-slate-500 mt-1 space-x-3">
                                      <span>ID: <code className="bg-slate-200 px-1 rounded">{sub.subscription_id}</code></span>
                                      <span>Created: {created}</span>
                                      {trialEnd && <span>Trial ends: {trialEnd}</span>}
                                    </div>
                                  </div>
                                  
                                  {!isDbSub && !sub.cancel_at_period_end && (
                                    <div className="flex gap-2 ml-4">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="text-orange-600 border-orange-300 hover:bg-orange-50"
                                        onClick={() => handleCancelSubscription(sub.subscription_id, false)}
                                        disabled={cancelingSubId === sub.subscription_id}
                                      >
                                        {cancelingSubId === sub.subscription_id ? (
                                          <RefreshCw className="w-3 h-3 animate-spin" />
                                        ) : (
                                          'Cancel at Period End'
                                        )}
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="text-red-600 border-red-300 hover:bg-red-50"
                                        onClick={() => handleCancelSubscription(sub.subscription_id, true)}
                                        disabled={cancelingSubId === sub.subscription_id}
                                      >
                                        {cancelingSubId === sub.subscription_id ? (
                                          <RefreshCw className="w-3 h-3 animate-spin" />
                                        ) : (
                                          'Cancel Now'
                                        )}
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        
                        <p className="text-xs text-slate-500 mt-3">
                          Stripe Customer: <code className="bg-slate-200 px-1 rounded">{dup.stripe_customer_id}</code>
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                
                {duplicateSubs.length === 0 && !isLoadingDuplicates && (
                  <p className="text-sm text-slate-500">
                    Click &quot;Scan for Duplicates&quot; to check all users for duplicate Stripe subscriptions.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'errors' && (
          <div className="space-y-4" data-testid="admin-errors-tab">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Error Log</h2>
                <p className="text-sm text-slate-500">User-facing crashes and failures. Critical ones also email you (max once/hour per issue).</p>
              </div>
              <Button variant="outline" size="sm" onClick={loadErrorLogs} data-testid="refresh-errors-btn">
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingErrors ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>
            {loadingErrors ? (
              <p className="text-sm text-slate-400 py-8 text-center">Loading…</p>
            ) : errorLogs.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Check className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                  <p className="font-semibold text-slate-800">No open errors</p>
                  <p className="text-sm text-slate-500">You&apos;re all clear. New crashes will appear here automatically.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {errorLogs.map((err) => (
                  <Card key={err.id} data-testid={`error-log-${err.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className="bg-red-100 text-red-700">{err.source}</Badge>
                            {err.count > 1 && <Badge variant="outline">seen {err.count}×</Badge>}
                            <span className="text-xs text-slate-400">{formatDate(err.last_seen)}</span>
                          </div>
                          <p className="font-semibold text-slate-900 mt-1.5 break-words">{err.message}</p>
                          <p className="text-xs text-slate-500 mt-1 break-all">{err.url}</p>
                          {err.contractor_email && (
                            <p className="text-xs text-slate-500 mt-0.5">Contractor: {err.contractor_email}</p>
                          )}
                        </div>
                        <Button variant="outline" size="sm" className="flex-shrink-0" onClick={() => handleResolveError(err.id)} data-testid={`resolve-error-${err.id}`}>
                          <Check className="w-4 h-4 mr-1" /> Resolve
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

      </div>
      <DemoRequestModal
        open={demoLinkOpen}
        onClose={() => setDemoLinkOpen(false)}
        title="Send a demo link"
        subtitle="Email a prospect the interactive demo link + passcode."
        submitLabel="Send demo link"
        successTitle="Demo link sent"
        footnote="We'll email them the demo link and passcode from noreply@drawready.com."
      />
    </div>
  );
};

export default Admin;
